// pages/monitor/panels/peak/PeakHome.js —— 削峰填谷 · 首页
// 依据《云平台单站控制（削峰填谷）需求文档》p1–2 与文档截图（p001_i000 已读图核对）：三栏 + 底部通栏
//   左栏 概览数据（8 项指标）＋ 电站信息 tab
//   中栏 能量流转（电网→变压器→母线→储能/光伏/负载 2D 示意 + 即时数据浮窗）
//   右栏 告警状态（云站通信 + 名称/告警/通信 设备表）
//   底部通栏 实时运行（今日 0 点~次日 06:00 最小 1 分钟；双 Y 轴：电网/负载/储能功率 + SOC + 策略功率 + 需量限制；谷平峰时段条；放大/设置）
import React, { useEffect, useMemo, useState } from 'react';
import { Tag, Button, Space, Modal, Checkbox, Select, Table, message } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import { ZoomInOutlined, SettingOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { getOverview, getDevices, getPowerCurve } from '../../../../config/stationData';
import { Ef2NodeCard, ef2Pct } from '../../../../components/TopoGlbIcon';
import { getMinuteRealtime } from './data';
import './peak.css';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const LEGEND = { wrapperStyle: { fontSize: 11, color: 'rgba(255,255,255,0.6)' }, iconSize: 8 };
const GRID = 'rgba(255,255,255,0.08)';
const COMM_TEXT = { normal: '正常', delay: '延迟', offline: '离线' };
const COMM_COLOR = { normal: 'green', delay: 'orange', offline: 'red' };

const CURVE_OPTIONS = [
  { key: 'grid', label: '电网功率' },
  { key: 'load', label: '负载功率' },
  { key: 'storage', label: '储能功率' },
  { key: 'pv', label: '光伏功率' },
  { key: 'soc', label: 'SOC' },
  { key: 'strategy', label: '策略功率' },
];

// 谷平峰分时时段条（按 24 小时切分，仅作图例标注）
const SEGMENTS = [
  { label: '谷', type: 'valley', range: '00-06' },
  { label: '平', type: 'flat', range: '06-08' },
  { label: '峰', type: 'peak', range: '08-11' },
  { label: '平', type: 'flat', range: '11-13' },
  { label: '峰', type: 'peak', range: '13-15' },
  { label: '平', type: 'flat', range: '15-17' },
  { label: '峰', type: 'peak', range: '17-21' },
  { label: '平', type: 'flat', range: '21-22' },
  { label: '谷', type: 'valley', range: '22-24' },
];

// 能量流转：连线 SVG；节点卡 HTML + 右侧 GLB（变压器暂留空）
const EF_W = 900;
const EF_H = 300;
const EnergyFlow2D = ({ grid, storage, storageState, pv, load, transformer, hasPv }) => {
  const cx = EF_W / 2;
  const busY = 150;
  const boxY = 206;
  const boxH = 74;
  const boxW = 152;
  const nodes = [{
    name: '储能', val: `${storage}`, unit: 'kW', sub: storageState, device: 'storage',
    color: storageState === '充电' ? '#00d4ff' : storageState === '放电' ? '#52e8b0' : 'rgba(255,255,255,0.6)',
    up: storageState !== '充电',
  }];
  if (hasPv) nodes.push({ name: '光伏', val: `${pv}`, unit: 'kW', sub: '发电', color: '#f4c542', up: true, device: 'pv' });
  nodes.push({ name: '负载', val: `${load}`, unit: 'kW', sub: '用电', color: '#ff5a7f', up: false, device: 'load' });
  const left = 180;
  const right = EF_W - 180;
  const step = nodes.length > 1 ? (right - left) / (nodes.length - 1) : 0;
  const gridDown = grid >= 0;

  return (
    <div className="ef2-wrap">
      <svg className="ef2-svg" viewBox={`0 0 ${EF_W} ${EF_H}`} role="img" aria-label="能量流转主接线示意">
        <defs>
          <marker id="peak-down" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
            <path d="M0,0 L9,4.5 L0,9 z" fill="#00d4ff" />
          </marker>
          <marker id="peak-up" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
            <path d="M0,0 L9,4.5 L0,9 z" fill="#52e8b0" />
          </marker>
        </defs>
        <line className="ef2-link" x1={cx} y1={66} x2={cx} y2={88} markerEnd="url(#peak-down)" />
        <line className="ef2-link" x1={cx} y1={130} x2={cx} y2={busY} markerEnd="url(#peak-down)" />
        <line className="ef2-bus" x1={64} y1={busY} x2={EF_W - 64} y2={busY} />
        <text className="ef2-bus-label" x={64} y={busY - 12}>交流母线 0.4 kV</text>
        {nodes.map((n, i) => {
          const x = left + step * i;
          return (
            <g key={n.name}>
              <circle cx={x} cy={busY} r={4.5} fill="#00d4ff" />
              <line className="ef2-link" x1={x} y1={n.up ? boxY : busY} x2={x} y2={n.up ? busY : boxY}
                markerEnd={`url(#peak-${n.up ? 'up' : 'down'})`} />
            </g>
          );
        })}
      </svg>
      <Ef2NodeCard
        name="电网（公共连接点）"
        value={grid}
        unit="kW"
        valueColor={gridDown ? '#00d4ff' : '#52e8b0'}
        device="grid"
        style={ef2Pct(cx - 104, 10, 208, 56)}
      />
      <Ef2NodeCard
        name="变压器"
        sub={transformer}
        device={null}
        style={ef2Pct(cx - 82, 88, 164, 42)}
      />
      {nodes.map((n, i) => {
        const x = left + step * i;
        return (
          <Ef2NodeCard
            key={n.name}
            name={n.name}
            value={n.val}
            unit={n.unit}
            sub={n.sub}
            valueColor={n.color}
            device={n.device}
            style={ef2Pct(x - boxW / 2, boxY, boxW, boxH)}
          />
        );
      })}
    </div>
  );
};

const PeakHome = ({ station }) => {
  const ov = getOverview(station.id);
  const dev = getDevices(station.id);

  // 概览统计时间：进入页面获取一次，之后每 10s 自动刷新
  const [statTime, setStatTime] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setStatTime(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);
  const fmtTime = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${d.toTimeString().slice(0, 8)}`;

  const pvPower = station.has.pv ? ov.pvPower : 0;
  const hasBranchActive = dev.pcs.some((p) => p.running) || dev.racks.some((r) => r.state !== '静置');
  const storageState = !hasBranchActive ? '静置' : ov.storagePower > 0 ? '充电' : ov.storagePower < 0 ? '放电' : '静置';

  // 概览数据 · 8 项指标（对齐文档截图 p001_i000：当日充/放电、总充/放电、昨日收益、总收益、系统效率、电站年龄）
  const kpis = [
    { label: '当日充电量', value: ov.chargeToday.toLocaleString(), unit: 'kWh', foot: '当日累计' },
    { label: '当日放电量', value: ov.dischargeToday.toLocaleString(), unit: 'kWh', foot: '当日累计' },
    { label: '总充电量', value: ov.chargeTotal, unit: 'MWh', foot: '电量报表汇总' },
    { label: '总放电量', value: ov.dischargeTotal, unit: 'MWh', foot: '电量报表汇总' },
    { label: '昨日收益', value: `¥${ov.incomeYesterday.toLocaleString()}`, unit: '', foot: '收益报表昨日' },
    { label: '总收益', value: `¥${ov.incomeTotal.toLocaleString()}`, unit: '', foot: '收益报表汇总' },
    { label: '系统效率', value: ov.efficiency, unit: '%', foot: '总放电量 / 总充电量' },
    { label: '电站年龄', value: station.age, unit: '天', foot: `投运 ${station.onlineAt}` },
  ];

  // 能量流转 · 即时数据浮窗
  const flowChips = [
    { label: '储能充放电状态', value: storageState, cls: storageState === '充电' ? 'chg' : storageState === '放电' ? 'dis' : 'flat' },
    { label: '储能实时功率', value: `${ov.storagePower} kW`, cls: ov.storagePower > 0 ? 'chg' : ov.storagePower < 0 ? 'dis' : 'flat' },
    { label: 'SOC', value: `${ov.soc} %`, cls: 'flat' },
    { label: '可用电量', value: `${ov.availableEnergy} kWh`, cls: 'flat' },
    { label: '电网实时功率', value: `${ov.gridPower} kW`, cls: 'flat' },
    { label: '负载实时功率', value: `${ov.loadPower} kW`, cls: 'dis' },
  ];

  // 电站信息
  const infoBasic = [
    ['电站名称', station.name],
    ['电站类型', station.typeLabel],
    ['位置', station.location],
    ['投运时间', station.onlineAt],
    ['电站年龄', `${station.age} 天`],
    ['装机功率', station.rated.power],
    ['装机容量', station.rated.capacity],
    ['电压等级', station.grid.voltage],
    ['变压器', station.grid.transformer],
    ['光伏 / 充电桩', `${station.has.pv ? '有' : '无'} / ${station.has.charger ? '有' : '无'}`],
  ];
  const infoParam = [
    ['PCS 台数', `${dev.pcs.length} 台`],
    ['电池堆数量', `${dev.racks.length} 堆`],
    ['可放电量合计', `${dev.racks.reduce((s, r) => s + r.available, 0).toLocaleString()} kWh`],
    ['电表数量', `${dev.meters.length} 只`],
    ['空调台数', `${dev.hvac.length} 台`],
    ['通信状态', `${COMM_TEXT[station.comm]}`],
  ];
  const kvRows = (rows) => (
    <div className="dev-kv">
      {rows.map(([k, v]) => (
        <div className="dev-kv-row" key={k}><span className="dev-kv-k">{k}</span><span className="dev-kv-v">{v}</span></div>
      ))}
    </div>
  );

  // 告警状态 · 设备状态表（对齐截图：名称 / 告警 / 通信，PCS / 储能表 / 总表 / BMS堆 / 液冷）
  const commAlarm = station.comm !== 'normal';
  const deviceRows = [];
  dev.pcs.forEach((p) => deviceRows.push({ key: p.id, name: p.id, alarm: p.alarm ? '告警' : '正常', comm: p.running ? '正常' : '离线' }));
  dev.meters.forEach((m, i) => deviceRows.push({ key: m.id, name: i === 0 ? `${m.id}总表` : m.name, alarm: m.state === '正常' ? '正常' : '告警', comm: '正常' }));
  dev.racks.forEach((r, i) => deviceRows.push({ key: r.id, name: `BMS-${i + 1}#堆`, alarm: r.alarm ? '告警' : '正常', comm: r.state === '静置' || r.state === '放电' ? '正常' : '离线' }));
  deviceRows.push({ key: dev.liquid.id, name: '液冷机组', alarm: dev.liquid.state === '正常' ? '正常' : '告警', comm: '正常' });
  const devCols = [
    { title: '名称', dataIndex: 'name', key: 'name', render: (v) => <span style={{ color: 'rgba(255,255,255,0.85)' }}>{v}</span> },
    { title: '告警', dataIndex: 'alarm', key: 'alarm', render: (v) => <Tag color={v === '正常' ? 'green' : 'red'}>{v}</Tag> },
    { title: '通信', dataIndex: 'comm', key: 'comm', render: (v) => <Tag color={COMM_COLOR[v]}>{v}</Tag> },
  ];

  // 实时运行曲线（1 分钟颗粒度，双 Y 轴）
  const minute = useMemo(() => getMinuteRealtime(station), [station]);
  const curveRows = useMemo(
    () => minute.map((r) => ({ clock: r.clock, m: r.m,
      grid: r.grid, load: r.load, storage: r.storage,
      pv: station.has.pv ? r.pv : null, soc: r.soc, strategy: r.strategy })),
    [minute, station.has.pv],
  );
  const demandLimit = getPowerCurve(station.id)[0].demandLimit;
  const [visible, setVisible] = useState(['grid', 'load', 'storage', 'strategy', 'soc']);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [setOpen, setSetOpen] = useState(false);
  const [zoomDay, setZoomDay] = useState('今日');

  const renderLines = (withSoc = true) => (
    <>
      {visible.includes('grid') ? <Line yAxisId="left" type="stepAfter" dataKey="grid" name="电网功率" stroke="#4dd8ff" dot={false} strokeWidth={2} /> : null}
      {visible.includes('load') ? <Line yAxisId="left" type="stepAfter" dataKey="load" name="负载功率" stroke="#ff5a7f" dot={false} strokeWidth={2} /> : null}
      {visible.includes('storage') ? <Line yAxisId="left" type="stepAfter" dataKey="storage" name="储能功率" stroke="#52e8b0" dot={false} strokeWidth={2} /> : null}
      {station.has.pv && visible.includes('pv') ? <Line yAxisId="left" type="stepAfter" dataKey="pv" name="光伏功率" stroke="#f4c542" dot={false} strokeWidth={2} /> : null}
      {visible.includes('strategy') ? <Line yAxisId="left" type="stepAfter" dataKey="strategy" name="策略功率" stroke="#cbd5ff" strokeDasharray="6 4" dot={false} strokeWidth={1.5} /> : null}
      {withSoc && visible.includes('soc') ? <Area yAxisId="right" type="linear" dataKey="soc" name="SOC %" stroke="#8b7cf6" fill="url(#peakSoc)" strokeWidth={2} /> : null}
    </>
  );

  const curveChart = (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={curveRows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="peakSoc" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8b7cf6" stopOpacity={0.45} />
            <stop offset="95%" stopColor="#8b7cf6" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="clock" interval={119} tick={TICK} axisLine={false} tickLine={false} />
        <YAxis yAxisId="left" tick={TICK} axisLine={false} tickLine={false} width={48} />
        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} width={38} />
        <Tooltip contentStyle={TT} />
        <Legend {...LEGEND} />
        <ReferenceLine yAxisId="left" y={0} stroke="rgba(255,255,255,0.35)" />
        {/* 需量限制：取当前时间点公共连接点的需量保护值 */}
        <ReferenceLine yAxisId="left" y={demandLimit} stroke="#f4c542" strokeDasharray="5 5"
          label={{ value: `需量限制 ${demandLimit} kW`, fill: '#f4c542', fontSize: 11, position: 'insideTopRight' }} />
        {renderLines(true)}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const sectionTitle = (title, sub, actions) => (
    <div className="panel-title">
      <span>{title}<small>{sub}</small></span>
      {actions}
    </div>
  );

  return (
    <div>
      {/* 顶部三栏：概览数据 | 能量流转 | 告警状态 */}
      <div className="peak-home-top">
        {/* ① 概览数据（含电站信息 tab） */}
        <div className="panel peak-home-col">
          {sectionTitle('概览数据', `统计时间：${fmtTime(statTime)}·10s 自动刷新`)}
          {/* 子页切换对齐光储充「控制中心」顶部胶囊 Tabs */}
          <ScrollTabs
            items={[
              {
                key: 'overview', label: '概览数据', children: (
                  <div className="pk-kpi-grid">
                    {kpis.map((k) => (
                      <div className="pk-kpi" key={k.label}>
                        <div className="pk-kpi-label">{k.label}</div>
                        <div className="pk-kpi-value">{k.value}<span className="unit">{k.unit}</span></div>
                        <div className="pk-kpi-foot">{k.foot}</div>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'info', label: '电站信息', children: (
                  <ScrollTabs
                    items={[
                      { key: 'basic', label: '基本信息', children: <div className="pk-info-kv">{kvRows(infoBasic)}</div> },
                      { key: 'param', label: '运行参数', children: <div className="pk-info-kv">{kvRows(infoParam)}</div> },
                    ]}
                  />
                ),
              },
            ]}
          />
        </div>

        {/* ② 能量流转 */}
        <div className="panel peak-home-canvas">
          {sectionTitle('能量流转', '即时功率方向 · 储能充放电状态')}
          <EnergyFlow2D
            grid={ov.gridPower} storage={ov.storagePower} storageState={storageState}
            pv={pvPower} load={ov.loadPower} transformer={station.grid.transformer} hasPv={station.has.pv}
          />
          <div className="pk-flow-chips">
            {flowChips.map((c) => (
              <div className="pk-chip" key={c.label}>
                <div className="pk-chip-label">{c.label}</div>
                <div className={`pk-chip-value ${c.cls}`}>{c.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ③ 告警状态：云站通信 + 设备表 */}
        <div className="panel peak-home-col">
          {sectionTitle('告警状态', '云站通信 · 设备告警与通信')}
          <div className="pk-comm-line">
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>云站通信</span>
            <span style={{ fontSize: 13 }}>
              <span className={`dot ${commAlarm ? 'offline' : 'online'}`} style={{ marginRight: 6 }} />
              {commAlarm ? '异常' : '正常'}
            </span>
          </div>
          <Table
            size="small" rowKey="key" columns={devCols} dataSource={deviceRows}
            pagination={false} className="pk-dev-table"
          />
        </div>
      </div>

      {/* 底部通栏：实时运行 */}
      <div className="panel">
        {sectionTitle('实时运行', '今日 0 点 ~ 次日 06:00 · 最小颗粒度 1 分钟 · 双 Y 轴',
          <Space size={6}>
            <Button size="small" className="panel-title-btn" icon={<ZoomInOutlined />} onClick={() => setZoomOpen(true)}>放大</Button>
            <Button size="small" className="panel-title-btn" icon={<SettingOutlined />} onClick={() => setSetOpen(true)}>设置</Button>
          </Space>)}
        <div className="pk-segments">
          {SEGMENTS.map((s) => (
            <div key={s.range} className={`pk-seg pk-seg-${s.type}`} title={`${s.label} ${s.range}`}>{s.label}</div>
          ))}
        </div>
        {curveChart}
        <div className="peak-note">
          策略功率按预置策略模板绘制，覆盖「当前时间 ~ 次日 06:00」；需量限制为公共连接点当前需量保护值。点击「放大」可按天回看曲线，「设置」可配置此处展示的曲线。
        </div>
      </div>

      {/* 放大：按天回看曲线 */}
      <Modal title="实时运行 · 按天回看" open={zoomOpen} onCancel={() => setZoomOpen(false)} footer={null} width={880} destroyOnHidden>
        <div className="peak-toolbar">
          <span className="peak-toolbar-label">选择日期</span>
          <Select value={zoomDay} onChange={setZoomDay} style={{ width: 200 }}
            options={[{ value: '今日', label: '今日' }, { value: '昨日', label: '昨日' }, { value: '前日', label: '前日' }]} />
        </div>
        {curveChart}
      </Modal>

      {/* 设置：配置此处需要展示的曲线 */}
      <Modal title="实时运行 · 曲线设置" open={setOpen} onOk={() => { setSetOpen(false); message.success('曲线展示项已更新（演示）'); }}
        okText="确定" cancelText="取消" onCancel={() => setSetOpen(false)} destroyOnHidden>
        <Checkbox.Group
          value={visible} onChange={setVisible}
          options={CURVE_OPTIONS.map((c) => ({ label: c.label, value: c.key, disabled: c.key === 'pv' && !station.has.pv }))}
        />
      </Modal>
    </div>
  );
};

export default PeakHome;